import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getSubscriptionInfo, Subscription } from '../../utils/api/natureDrApi';
import './Pricing.css';

const BENEFITS = [
  'Unlimited natural health advice',
  'Personalized remedies & tips',
  'Priority support',
  '7 day free trial, cancel anytime',
];

const PRICING_PLANS = [
  {
    name: 'Weekly',
    price: '£2',
    period: 'per week',
    highlight: false,
    button: 'Choose Weekly',
    priceId: 'price_1RWmPUKkQIsBaad2XTXDKNIM',
  },
  {
    name: 'Yearly',
    price: '£40',
    period: 'per year',
    highlight: true,
    badge: 'Save over 50%!',
    button: 'Choose Yearly',
    priceId: 'price_1RWmPzKkQIsBaad29S3aglBt',
  },
];

const Pricing: React.FC = () => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const auth = getAuth();

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!auth.currentUser) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await getSubscriptionInfo();
        
        if (response.subscriptions && response.subscriptions.length > 0) {
          setSubscription(response.subscriptions[0]);
        }
      } catch (err: any) {
        // Silent failure - we don't want to show subscription errors on the pricing page
        // Don't set error - we don't want to show this on the pricing page
      } finally {
        setLoading(false);
      }
    };
    
    fetchSubscription();
  }, [auth.currentUser]);

  return (
    <div className="pricing-container">
      <h1>Pricing</h1>
      <div className="benefits-section">
        <h2 className="benefits-title">All plans include:</h2>
        <ul className="pricing-benefits">
          {BENEFITS.map((benefit) => (
            <li key={benefit}>{benefit}</li>
          ))}
        </ul>
      </div>
      <div className="pricing-cards">
        {PRICING_PLANS.map((plan) => (
          <PricingCard 
            plan={plan} 
            key={plan.name} 
            subscription={subscription}
            isLoading={loading}
          />
        ))}
      </div>
    </div>
  );
};

// --- PricingCard component ---
function PricingCard({ 
  plan, 
  subscription, 
  isLoading 
}: { 
  plan: typeof PRICING_PLANS[0], 
  subscription: Subscription | null,
  isLoading: boolean 
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const auth = getAuth();
  const user = auth.currentUser;

  const handleSubscribe = async () => {
    setError(null);
    setLoading(true);
    try {
      if (!user) {
        setError('You must be signed in to subscribe.');
        setLoading(false);
        return;
      }
      const token = await user.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          priceId: plan.priceId,
          email: user.email,
        }),
      });
      if (!res.ok) throw new Error('Failed to create checkout session');
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No session URL returned');
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
      setLoading(false);
    }
  };

  // Check if the user is already subscribed to this plan
  const isCurrentPlan = subscription && subscription.plan && 
    (plan.priceId === subscription.plan.id || 
    (subscription.plan.nickname && plan.name.toLowerCase() === subscription.plan.nickname.toLowerCase()));

  return (
    <div className={`pricing-card${plan.highlight ? ' highlight' : ''}${isCurrentPlan ? ' current-plan' : ''}`}>
      {plan.highlight && (
        <div className="pricing-badge">{plan.badge}</div>
      )}
      <div className="pricing-header">
        <div className="pricing-title">{plan.name}</div>
      </div>
      <div className="pricing-price">
        <div className="pricing-amount">{plan.price}</div>
      </div>
      <div className="pricing-period-container">
        <div className="pricing-period">{plan.period}</div>
      </div>
      {isCurrentPlan ? (
        <div 
          className="current-plan-badge clickable"
          onClick={() => navigate('/settings')}
          role="button"
          tabIndex={0}
          aria-label="View subscription details"
        >
          <span>Current Plan - View Details</span>
        </div>
      ) : (
        <button
          className="choose-plan-btn"
          onClick={handleSubscribe}
          disabled={loading || !user || isLoading}
        >
          {loading ? 'Redirecting...' : plan.button}
        </button>
      )}
      {error && <div className="pricing-error">{error}</div>}
    </div>
  );
}

export default Pricing;
